import React, { useRef, useState, useEffect } from 'react';

interface SimpleAudioButtonProps {
  audioSrc: string;
  className?: string;
}

const SimpleAudioButton: React.FC<SimpleAudioButtonProps> = ({ audioSrc, className = '' }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasEnteredView, setHasEnteredView] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  
  // Set up intersection observer to detect when the button enters viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setHasEnteredView(true);
        }
      },
      { threshold: 0.5 }
    );
    
    if (buttonRef.current) {
      observer.observe(buttonRef.current);
    }
    
    return () => {
      if (buttonRef.current) {
        observer.unobserve(buttonRef.current);
      }
    };
  }, []);
  
  // Trigger animation once when the component enters the viewport
  useEffect(() => {
    if (hasEnteredView && !hasAnimated) {
      setHasAnimated(true);
    }
  }, [hasEnteredView, hasAnimated]);
  
  const togglePlayPause = () => {
    if (!audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };
  
  const handlePlay = () => {
    setIsPlaying(true);
  };
  
  const handlePause = () => {
    setIsPlaying(false);
  };
  
  const handleEnded = () => {
    setIsPlaying(false);
  };
  
  return (
    <div className={`flex flex-col items-center group ${className}`}>
      <button 
        ref={buttonRef}
        onClick={togglePlayPause}
        // Increased size from w-10 h-10 to w-12 h-12 (20% bigger)
        className={`w-12 h-12 rounded-full bg-[#C2A4D6] flex items-center justify-center hover:bg-[#b390c9] transition-colors focus:outline-none focus:ring-2 focus:ring-[#C2A4D6] focus:ring-opacity-50 shadow-md relative overflow-hidden group-hover:ring-2 group-hover:ring-[#C2A4D6] group-hover:ring-opacity-30
          ${hasEnteredView && !hasAnimated ? 'animate-pulse' : ''}`}
        aria-label={isPlaying ? "Pause" : "Play"}
      >
        {/* Subtle glow effect on hover */}
        <span className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity"></span>
        
        {/* Pulsing animation when playing */}
        {isPlaying && (
          <span className="absolute inset-0 bg-white opacity-10 animate-pulse" style={{ animationDuration: '2s' }}></span>
        )}
        
        {/* Initial attention animation */}
        {hasEnteredView && !hasAnimated && (
          <span className="absolute inset-0 bg-white opacity-20 animate-ping" 
                style={{ animationDuration: '1s', animationIterationCount: 3 }}
                onAnimationEnd={() => setHasAnimated(true)}></span>
        )}
        
        {isPlaying ? (
          <span className="text-white z-10">
            {/* Increased icon size from 24 to 28 */}
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="6" y="4" width="4" height="16"></rect>
              <rect x="14" y="4" width="4" height="16"></rect>
            </svg>
          </span>
        ) : (
          <span className="text-white ml-1 z-10">
            {/* Increased icon size from 24 to 28 */}
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
          </span>
        )}
      </button>
      
      {/* Text label below button - "Listen" */}
      <span className="text-xs text-[#C2A4D6] font-medium mt-1">
        {isPlaying ? "Playing..." : "Listen"}
      </span>
      
      <audio 
        ref={audioRef}
        className="hidden"
        onPlay={handlePlay}
        onPause={handlePause}
        onEnded={handleEnded}
      >
        <source src={audioSrc} type="audio/mp3" />
        Your browser does not support audio playback.
      </audio>
    </div>
  );
};

export default SimpleAudioButton;